import ProductApi from "@api/productApi";
import Button from "@elements/button";
import Carousel from "@elements/carousel";
import SimpleListBox from "@elements/simpleListBox";
import Filters from "@modules/attribute/filters";
import ToyIcon from "@svg/ToyIcon";
import {
  IFilterAndSortInput,
  IFilterInput,
  IFilterOutput,
  IProductDetailModel,
  IProductList,
  ISortInput,
  ISortOutput,
} from "@types";
import { setRoute, useIsMount } from "@utils/helpers";
import Loading from "assets/lottie/loading";
import { remove } from "lodash";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { SwiperSlide } from "swiper/react";
import MobileFilter from "@modules/product/newFilter";
import { useAppContext } from "@context/app";
import DynamicProductCard from "@elements/dynamicProductCard/dynamicProductCard";
import useProduct from "@hooks/useProduct";

interface Props {
  serverFilter?: IFilterOutput;
  serverSort?: ISortOutput;
  serverSearch?: string;
  clientFilter: IFilterOutput[];
  clientSecondaryFilter: IFilterOutput[];
  data: IProductList;
  filters?: IFilterAndSortInput;
}

const Product: React.FC<Props> = ({
  serverFilter,
  serverSort,
  serverSearch,
  clientFilter,
  data,
  clientSecondaryFilter,
  filters
}) => {
  const t = useTranslations("");
  const isMount = useIsMount();
  const { segmentFilterEvent } = useProduct()
  const { setEventProperties } = useAppContext()
  const [source, setSource] = useState<string>("");
  const [sourceSlug, setSourceSlug] = useState<string>("");
  const [productList, setProductList] = useState<IProductDetailModel[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [curser, setCurser] = useState<string>("");
  const [isFetching, setIsFetching] = useState(false);
  const [selectedSecondaryFilter, setSelectedSecondaryFilter] = useState<{ title: string; filters: IFilterOutput[] } | undefined>();
  const [sortList, setSortList] = useState<ISortInput[]>([]);
  const [filterList, setFilterList] = useState<IFilterInput[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<IFilterOutput[]>([]);
  const [sort, setSort] = useState<ISortOutput | undefined>(serverSort || undefined);
  const [search] = useState<string | undefined>(serverSearch || undefined);
  const { query, pathname } = useRouter();
  const category = typeof query.category_id === "string" ? query.category_id : "";
  const attribute = typeof query.attributes_id === "string" ? query.attributes_id : "";
  const subcategory = typeof query.subcategory_id === "string" ? query.subcategory_id : "";
  const input = typeof query.input === "string" ? query.input : "";
  const brand = typeof query.brand_id === "string" ? query.brand_id : "";
  const tag = typeof query.tags_id === "string" ? query.tags_id : "";
  const collection = typeof query.collection_id === "string" ? query.collection_id : "";


  useEffect(() => {
    if (source)
      setEventProperties({
        from_page: source === "search" ? "search" : "product_list",
        from_entity: source === "search" ? "" : source,
        from_entity_slug: data.entity?.slug || "",
        from_page_section: "",
        from_page_section_entity: "",
        from_page_section_slug: "",
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, sourceSlug])

  useEffect(() => {

    if (input) {
      setSource("search")
    }
    if (collection) {
      setSource("collection")
    }
    if (category) {
      setSource("category")
    }
    if (brand) {
      setSource("brand")
    }
    if (attribute) {
      setSource("attribute")
    }
    if (tag) {
      setSource("tag")
    }
    setSourceSlug(data.entity?.slug || "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, collection, category, brand, attribute, tag]);
  useEffect(() => {
    if (data) {
      if (data.secondary_filters && subcategory) {
        setSelectedSecondaryFilter(
          data.secondary_filters?.items.find(
            (x) => x.filters[0].items && x.filters[0].items[0] === Number(subcategory)
          )
        );
      } else if (data.secondary_filters && clientSecondaryFilter.length) {
        setSelectedSecondaryFilter(
          data.secondary_filters?.items.find(
            (x) =>
              x.filters[0].items && x.filters[0].items[0] ===
              Number(
                clientSecondaryFilter[0]?.items
                  ? clientSecondaryFilter[0]?.items[0]
                  : 0
              )
          )
        );
      }
      setProductList([...data.items]);
      setHasMore(data.pagination.more_items);
      setCurser(data.pagination.cursor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (clientFilter.length) {
      const finalFilter = [];
      for (const iterator of clientFilter) {
        let data = { ...iterator };
        delete data.id;
        finalFilter.push(data);
      }

      setSelectedFilters([...finalFilter]);
    }
  }, [clientFilter]);

  useEffect(() => {
    if (filters && data) {
      const _sort = [...filters.sort];
      const filtersWithId = [];
      setSortList(_sort);
      for (const [index, iterator] of filters.filters.entries()) {
        filtersWithId.push({ ...iterator, id: index + 1 });
      }
      if (serverFilter) {
        const _filters = [...filtersWithId];
        const newFilterList = remove(_filters, (value) => {
          if (
            serverFilter.key === "attributes_id" &&
            serverFilter.items?.length
          ) {
            if (value.key !== "attributes_id") {
              return value;
            }
          } else {
            return value.key !== serverFilter.key;
          }
        });
        setFilterList([...newFilterList]);
      } else {
        setFilterList([...filtersWithId]);
      }
    }
  }, [filters, serverFilter, data]);

  const fetchData = async () => {
    setIsFetching(true);
    const _f = []
    for (const iterator of selectedFilters) {
      const i = { ...iterator }
      delete i.items_slug
      _f.push(i)
    }
    let filterForReq = [];
    if (serverFilter && selectedSecondaryFilter) {
      filterForReq = [serverFilter, ..._f, ...selectedSecondaryFilter.filters];
    } else if (serverFilter) {
      filterForReq = [serverFilter, ..._f];
    } else if (selectedSecondaryFilter) {
      filterForReq = [..._f, ...selectedSecondaryFilter.filters];
    } else {
      filterForReq = [..._f];
    }
    try {
      const res = await ProductApi.list(
        { sort: sort, filters: filterForReq, search },
        curser
      ).fetch();
      setProductList([...productList, ...res.data.items]);
      setHasMore(res.data.pagination.more_items);
      setIsFetching(false);

    } catch (error) {
      setIsFetching(false);
    }
  };

  const fetchNewData = async () => {
    setIsFetching(true);
    const _f = []
    for (const iterator of selectedFilters) {
      const i = { ...iterator }
      delete i.items_slug
      _f.push(i)
    }
    let filterForReq;

    if (serverFilter) {
      if (selectedSecondaryFilter) {
        if (selectedSecondaryFilter.filters.find(i => i.key === serverFilter.key)) {
          filterForReq = [..._f, ...selectedSecondaryFilter.filters];
        } else {
          filterForReq = [serverFilter, ..._f, ...selectedSecondaryFilter.filters];
        }
      } else {
        if (!category && subcategory && data.entity?.id && data.entity.type === "category") {
          filterForReq = [{
            key: "category_id",
            type: "singleSelect",
            operation_type: "include",
            items: [data.entity.id],
            items_operation: "or",
          }, ..._f] as any
        } else { filterForReq = [serverFilter, ..._f]; }

      }
    } else {
      if (selectedSecondaryFilter) {
        filterForReq = [..._f, ...selectedSecondaryFilter.filters];
      } else {
        filterForReq = [..._f];
      }
    }


    try {
      const res = await ProductApi.list({
        sort: sort,
        filters: filterForReq,
        search,
      }).fetch();
      setProductList([...res.data.items]);
      setHasMore(res.data.pagination.more_items);
      setCurser(res.data.pagination.cursor);
      setIsFetching(false);
    } catch (error) {
      setIsFetching(false);
    } finally {
      window.scrollTo(0, 0);
    }
  };

  useEffect(() => {
    if (!isMount) {
      let params: { [key: string]: any } = {};

      if (subcategory) {
        if (serverFilter && category) {
          let filterValue;
          if (serverFilter.items) {
            filterValue = serverFilter.items?.map((x) => x)?.join(",");
          } else if (serverFilter.custom) {
            filterValue = serverFilter.custom;
          } else {
            filterValue = JSON.stringify(serverFilter.range);
          }

          params = {
            [serverFilter.key]: filterValue,
          };
        } else if (data.entity?.type === "category" && !selectedSecondaryFilter) {
          params = {
            category_id: data.entity.id,
          };
        }
      }
      else {
        if (serverFilter) {
          let filterValue;
          if (serverFilter.items) {
            filterValue = serverFilter.items?.map((x) => x)?.join(",");
          } else if (serverFilter.custom) {
            filterValue = serverFilter.custom;
          } else {
            filterValue = JSON.stringify(serverFilter.range);
          }

          params = {
            [serverFilter.key]: filterValue,
          };
        }
      }



      if (search) {
        params = { input: search }
      }

      if (sort) {
        params = { ...params, sortKey: sort.key, sortValue: sort.value };
      }

      if (selectedFilters.length) {
        let _filters = [];

        for (const iterator of selectedFilters) {
          let filterValue;

          if (iterator.items) {
            filterValue = iterator.items?.map((x) => x)?.join(",");
          } else if (iterator.custom) {
            filterValue = iterator.custom;
          } else {
            filterValue = iterator.range;
          }

          _filters.push({
            filterKey: iterator.key,
            filterValue: filterValue,
          });
        }

        params = { ...params, filters: JSON.stringify(_filters) };
      }

      if (selectedSecondaryFilter?.filters.length) {
        let _secFilters = [];
        for (const iterator of selectedSecondaryFilter.filters) {
          let filterValue;

          if (iterator.items) {
            filterValue = iterator.items?.map((x) => x)?.join(",");
          } else if (iterator.custom) {
            filterValue = iterator.custom;
          } else {
            filterValue = iterator.range;
          }

          if (serverFilter?.key === iterator.key) {
            params = { ...params, [iterator.key]: filterValue };
          }
          else {
            _secFilters.push({
              filterKey: iterator.key,
              filterValue: filterValue,
            });
          }
        }
        if (_secFilters.length) {
          params = { ...params, secondaryFilters: JSON.stringify(_secFilters) };
        }
      }

      setRoute(params, pathname);
      fetchNewData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, sort, selectedSecondaryFilter]);

  const handelSelectedSecondaryFilter = (
    data: { title: string; filters: IFilterOutput[] } | undefined
  ) => {
    if (!data) {
      setSelectedSecondaryFilter(undefined);
    } else {
      setSelectedSecondaryFilter(data);
    }
  };

  const setNewSort = (data: ISortInput) => {
    if (data.key !== "default") {
      setSort(() => ({ key: data.key, value: data.value }));
    } else {
      setSort(() => undefined);
    }
  };




  return (
    <div className="w-full  max-w-[1248px] px-2 m-auto">
      {isFetching && <Loading isCenter />}
      <div className="w-full ms:mt-10 xs:mt-5 mt-5 mb-5">
        <p className="text-[28px] font-extrabold text-[#333333] capitalize">
          {data.title || input}
        </p>
      </div>
      <div className=" w-full flex flex-wrap">
        <div className="flex flex-wrap h-[max-content] flex-1 overflow-x-hidden">
          <div className="w-full ">
            <div className=" w-full flex flex-wrap  justify-between items-center">
              {data.secondary_filters ? (
                <div className=" md:flex-1  w-full overflow-hidden">
                  <Carousel gap={10}>
                    <SwiperSlide id="all" className=" !w-max">
                      <button
                        className={`${!selectedSecondaryFilter
                          ? " border-[#008AF6] text-[#008AF6] border-2 bg-[#008AF6] bg-opacity-10"
                          : "border-[#eee] text-[#AAA] border"
                          }  rounded-full  h-10  px-5  font-normal text-sm !w-max`}
                        onClick={() => handelSelectedSecondaryFilter(undefined)}
                      >
                        {data.secondary_filters.all}
                      </button>
                    </SwiperSlide>
                    {data.secondary_filters.items.map((sec_filter, index) => (
                      <SwiperSlide
                        key={sec_filter.title}
                        className="w-max"
                        id={`${index}`}
                      >
                        <button
                          className={`${selectedSecondaryFilter?.title === sec_filter.title
                            ? " border-[#008AF6] text-[#008AF6] border-2 bg-[#008AF6] bg-opacity-10"
                            : "border-[#eee] text-[#AAA] border"
                            }  rounded-full  h-10  px-5  font-normal text-sm !w-max`}
                          onClick={() =>
                            handelSelectedSecondaryFilter(sec_filter)
                          }
                        >
                          {sec_filter.title}
                        </button>
                      </SwiperSlide>
                    ))}
                  </Carousel>
                </div>
              ) : (
                <div className="md:flex-1 md:block hidden"></div>
              )}
              {sortList.length ? (
                <div className=" w-[max-content] z-20 md:py-2 ">
                  <SimpleListBox
                    action={setNewSort}
                    items={sortList || []}
                    defaultSort={sort || data.sort}
                  />
                </div>
              ) : null}
              <div className=" md:hidden sm:w-max  xs:w-max w-max  overflow-x-hidden mt-[4px]">
                <MobileFilter
                  items={filterList}
                  action={(_filterOutput) => { setSelectedFilters([..._filterOutput]); segmentFilterEvent(_filterOutput, data, { filters, input, source }) }}
                  defaultFilters={clientFilter}
                />
              </div>
            </div>
          </div>
          {productList.length ? (
            <>
              <div className=" flex flex-wrap  w-full md:justify-start justify-center mt-5 md:gap-3 ">
                {productList.map((product) => (
                  <div className="w-1/2 max-w-[190px] md:p-0 p-1" key={product.default_sku_id}>
                    <DynamicProductCard product={product} source={input ? "search_list" : "product_list"} sourceEntity={input ? "" : source} sourceSlug={data.entity?.slug || ""} />
                  </div>
                ))}
              </div>
              {!isFetching && hasMore && (
                <div className="w-full mt-5">
                  <Button
                    size="slim"
                    className="m-auto h-12 font-extrabold text-base rounded-full flex px-2 w-[200px]"
                    onClick={() => fetchData()}
                  >
                    {t("w-loadMore")}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="w-full max-w-[480px] m-auto h-screen flex items-center justify-center md:max-h-[calc(100dvh-300px)] max-h-[calc(100dvh-130px)] ">
              <div className=" flex flex-col items-center">
                <p className=" text-[#999] ">
                  <ToyIcon className=" w-[50px] h-[50px] " stroke="#999" />
                </p>
                <p className=" text-base text-[#999] font-normal text-center mt-5">
                  {t("client_productlist_nodatamessage")}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="w-full md:block sm:hidden  xs:hidden hidden px-3 overflow-x-hidden max-w-[350px]">
          {filterList.length ? <Filters
            items={filterList}
            action={(_filterOutput) => { setSelectedFilters([..._filterOutput]); segmentFilterEvent(_filterOutput, data, { filters, input, source }) }}
            defaultFilters={clientFilter}
          /> : null}
        </div>
      </div>
    </div>
  );
};

export default Product;
